import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { ShieldX } from "lucide-react";

const AdminUserContext = createContext(null);
export const useAdminUser = () => useContext(AdminUserContext);

export default function ProtectedAdmin({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(
    location.state?.user ? { status: "ok", user: location.state.user } : { status: "checking", user: null }
  );

  useEffect(() => {
    if (location.state?.user) return;
    api
      .get("/auth/me")
      .then((res) => {
        if (["super_admin", "admin"].includes(res.data.role)) {
          setState({ status: "ok", user: res.data });
        } else {
          setState({ status: "denied", user: res.data });
        }
      })
      .catch(() => setState({ status: "anon", user: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login", { replace: true });
  };

  if (state.status === "checking") {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-flame border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (state.status === "anon") return <Navigate to="/admin/login" replace />;
  if (state.status === "denied") {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-6">
        <div className="max-w-md text-center rounded-2xl border border-white/10 bg-navy-800 p-10" data-testid="admin-access-denied">
          <ShieldX className="w-10 h-10 text-flame mx-auto" />
          <h1 className="mt-6 font-heading text-2xl font-bold">Access restricted</h1>
          <p className="mt-3 text-sm text-slate-400">
            {state.user?.email} is signed in but does not have admin access. Contact the site owner to be added as an admin.
          </p>
          <Button onClick={logout} data-testid="admin-denied-logout-btn" variant="outline" className="mt-8 border-white/20">
            Sign out
          </Button>
        </div>
      </div>
    );
  }
  return (
    <AdminUserContext.Provider value={{ user: state.user, logout }}>
      {children}
    </AdminUserContext.Provider>
  );
}
